import { z } from "zod"
import { marketplaceIdSchema } from "../marketplace-id"

export const RELIST_QUEUE_STATUS = {
  pending: "pending",
  claimed: "claimed",
  blocked: "blocked",
  enrolled: "enrolled",
} as const
export const PREPARATION_QUEUE_STATUS = {
  queued: "queued",
  running: "running",
  blocked: "blocked",
  paused: "paused",
  unsupported: "unsupported",
  ready: "ready",
  unknown: "unknown",
} as const
const queueQuerySchema = z.object({
  marketplace: marketplaceIdSchema.optional(),
  view: z.enum(["active", "recent"]).default("active"),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  before: z.coerce.number().int().positive().optional(),
})
export const relistQueueQuerySchema = queueQuerySchema
  .extend({
    status: z.enum(RELIST_QUEUE_STATUS).optional(),
    sku: z.string().trim().min(1).max(200).optional(),
  })
  .strict()
export const preparationQueueQuerySchema = queueQuerySchema
  .extend({
    status: z.enum(PREPARATION_QUEUE_STATUS).optional(),
    run: z.string().trim().min(1).max(200).optional(),
  })
  .strict()
export type RelistQueueQuery = z.infer<typeof relistQueueQuerySchema>
export type PreparationQueueQuery = z.infer<typeof preparationQueueQuerySchema>
const countSchema = z.number().int().nonnegative()
export const queueCountsSchema = z.object({
  total: countSchema,
  byStatus: z.record(z.string(), countSchema),
})
export const queueActivitySchema = z.object({
  source: z.literal("scheduled_job_runs"),
  scheduleEnabled: z.boolean().nullable(),
  scope: z.string(),
  observedAt: z.string(),
  latest: z
    .object({
      id: z.string(),
      status: z.string(),
      eventAt: z.string(),
      startedAt: z.string().nullable(),
      finishedAt: z.string().nullable(),
    })
    .nullable(),
  lastSuccessAt: z.string().nullable(),
  limitation: z.string(),
})
export const relistQueueRowSchema = z.object({
  id: z.number().int(),
  eventId: z.number().int(),
  marketplace: marketplaceIdSchema,
  configSku: z.string(),
  reason: z.string().nullable(),
  changedAttributes: z.array(z.string()),
  status: z.enum(RELIST_QUEUE_STATUS),
  blocker: z.string().nullable(),
  detectedAt: z.string().nullable(),
  recordedAt: z.string(),
  enrolledAt: z.string().nullable(),
  runId: z.number().int().nullable(),
  runSlug: z.string().nullable(),
  savedEnabled: z.boolean(),
  executionEnabled: z.boolean(),
  effectiveEnabled: z.boolean(),
  claimLive: z.boolean(),
})
export const sourceCoverageSchema = z.object({
  marketplace: marketplaceIdSchema,
  state: z.enum(["known", "never_activated", "unknown"]),
  unreadSourceEvents: countSchema.nullable(),
  scope: z.literal("fleet_source_events"),
  activationEventId: z.number().int().nullable(),
  lastSeenEventId: z.number().int().nullable(),
})
export const queueWaitingSchema = z.object({
  total: z.number().int().nonnegative(),
  oldestAt: z.string().nullable(),
  unknownCount: z.number().int().nonnegative(),
})

const queuePageSchema = z.object({
  observedAt: z.string(),
  waiting: queueWaitingSchema,
  nextBefore: z.number().int().nullable(),
  counts: queueCountsSchema,
  activity: z.array(queueActivitySchema),
  limitations: z.array(z.string()),
  marketplaces: z.array(z.object({ id: marketplaceIdSchema, name: z.string() })),
})
export const relistQueueSchema = queuePageSchema.extend({
  rows: z.array(relistQueueRowSchema),
  sourceCoverage: z.array(sourceCoverageSchema),
  pausedCount: countSchema,
})
export const preparationQueueRowSchema = z.object({
  id: z.number().int(),
  slug: z.string(),
  marketplace: marketplaceIdSchema,
  masterCategory: z.string().nullable(),
  activeProductCount: countSchema,
  inputRevision: countSchema,
  requestedRevision: countSchema.nullable(),
  status: z.enum(PREPARATION_QUEUE_STATUS),
  stage: z.string().nullable(),
  blocker: z.string().nullable(),
  executionEnabled: z.boolean(),
  scheduleEnabled: z.boolean().nullable(),
  supported: z.boolean(),
  ownedByOtherTask: z.boolean(),
  claimLive: z.boolean(),
  requestedAt: z.string().nullable(),
  originalWaitingAt: z.string().nullable(),
  unknownAgeCount: countSchema,
  uncoveredOutsideMembershipCount: countSchema,
  artifactId: z.number().int().nullable(),
  currentArtifactProven: z.boolean(),
  recentCompletedAt: z.string().nullable(),
  currentStageObservedAt: z.string().nullable(),
})
export const preparationQueueSchema = queuePageSchema.extend({
  rows: z.array(preparationQueueRowSchema),
})
export type RelistQueue = z.infer<typeof relistQueueSchema>
export type PreparationQueue = z.infer<typeof preparationQueueSchema>
export type PreparationQueueRow = z.infer<typeof preparationQueueRowSchema>
export type RelistQueueRow = z.infer<typeof relistQueueRowSchema>
